import React from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';

class PageErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the full stack in development without exposing technical details to users.
    console.error('Page rendering failed', error, info);
  }

  componentDidUpdate(previousProps) {
    const previousPath = previousProps.location?.pathname;
    const currentPath = this.props.location?.pathname;
    if (this.state.error && previousPath !== currentPath) {
      this.setState({ error: null });
    }
  }

  retry = () => {
    this.setState({ error: null });
  };

  goHome = () => {
    this.setState({ error: null }, () => this.props.history.replace('/'));
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Fallback role="alert">
        <ErrorOutlineRoundedIcon />
        <h1>Cette page n’a pas pu s’afficher</h1>
        <p>
          Vos données ne sont pas perdues. Réessayez l’affichage ou revenez à votre espace principal.
        </p>
        <Actions>
          <PrimaryButton type="button" onClick={this.retry}>
            <RefreshRoundedIcon /> Réessayer
          </PrimaryButton>
          <SecondaryButton type="button" onClick={this.goHome}>
            <HomeOutlinedIcon /> Retour à l’accueil
          </SecondaryButton>
        </Actions>
      </Fallback>
    );
  }
}

const Fallback = styled.section`
  width: min(calc(100% - 2rem), 44rem);
  min-height: 24rem;
  display: grid;
  place-items: center;
  align-content: center;
  gap: .8rem;
  margin: clamp(1rem, 4vw, 3rem) auto;
  padding: clamp(1.5rem, 5vw, 3rem);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: #fff;
  box-shadow: var(--shadow-sm);
  text-align: center;

  > svg { color: #9b5a34; font-size: 3rem; }
  h1 { margin: 0; font-size: clamp(1.55rem, 4vw, 2.2rem); }
  p { max-width: 34rem; margin: 0; color: var(--color-textMuted); line-height: 1.65; }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: .65rem;
  margin-top: .55rem;

  @media (max-width: 30rem) {
    width: 100%;
    flex-direction: column;
  }
`;

const BaseButton = styled.button`
  min-height: 2.85rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .45rem;
  padding: .7rem 1rem;
  border-radius: 999px;
  font: inherit;
  font-weight: 850;
  cursor: pointer;

  svg { font-size: 1.05rem; }

  @media (max-width: 30rem) { width: 100%; }
`;

const PrimaryButton = styled(BaseButton)`
  border: 1px solid #111;
  background: #111;
  color: #fff;
`;

const SecondaryButton = styled(BaseButton)`
  border: 1px solid var(--color-borderStrong);
  background: #fff;
  color: #111;
`;

export default withRouter(PageErrorBoundary);
